import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, Renderer2, ViewChild, ViewEncapsulation, inject } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { debounce } from 'lodash'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { SelectorBasedSlotDirective } from '../../directives'

export type FormElement = NgElement & WithProperties<FormComponent>

/**
 * Component that will nicely group inputs and actions, as well as properly keep track of the value of
 * all slotted and direct child elements.
 */
@Component({
  selector: 'gpei-form',
  templateUrl: './form.component.html',
  styleUrls: [ './form.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [
    CommonModule,
    SelectorBasedSlotDirective
  ]
})
export class FormComponent {

  private _redirect: string
  private _debounceTimer = 0
  private _unlistenCallbacks: Array<() => void> = []
  private _formData: FormData = new FormData()
  private _promptOnLeave: boolean

  @ViewChild('inputSlot')
  private readonly _inputSlotRef: ElementRef<HTMLSlotElement>

  @ViewChild('actionsSlot')
  private readonly _actionsSlotRef: ElementRef<HTMLSlotElement>

  @ViewChild('form')
  private readonly _formRef: ElementRef<HTMLFormElement>

  private readonly _renderer2 = inject(Renderer2)
  private readonly _elementRef = inject(ElementRef<HTMLElement>)

  actionSlotSelector = 'button, .btn, input[type=submit]'
  submitSelector = 'button[type=submit], input[type=submit]'

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('gpeiFormData')
  formData: EventEmitter<FormData> = new EventEmitter()

  /**
   * Lifecycle hook for when the form is submitted.
   *
   * It will stop the propagation of the original event, and check the validity of all
   * slotted child elements of the form before emitting a {@link FormDataEvent}.
   *
   * @param event The insighting event that triggers this lifecycle hook
   */
  onSubmit(event: SubmitEvent): void {
    event.preventDefault()

    // Report the validity of the form
    let valid = this.reportValidity()

    // Create and emit the form data as both an output and native HTML event if the form is valid
    if (valid) {
      let debounced = debounce(
        () => {
          this.formData.emit(this._formData)
          let formDataEvent = new FormDataEvent('formdata', {
            bubbles: true,
            cancelable: true,
            formData: this._formData
          })
          this._elementRef.nativeElement.dispatchEvent(formDataEvent)
        },
        this.debounceTime,
        { leading: true, trailing: false }
      )

      debounced()
    }
  }

  /**
   * Lifecycle hook for when a slotted child element of the form emits an {@link InputEvent}.
   *
   * It will append the value of the input to the form data of the component.
   *
   * @param event The insighting event that triggers the lifecycle hook
   */
  onInput(event: InputEvent): void {
    let input = event.target as HTMLInputElement
    this._formData.set(input.name, input.value)
    if(this._promptOnLeave){
      let formDataChangeEvent = new CustomEvent('formdatachange', {
        bubbles: true
      })
      this._elementRef.nativeElement.dispatchEvent(formDataChangeEvent)
    }
  }

  /**
   * Lifecycle hook for when an element in the component is clicked.
   *
   * If the clicked element is a button that matches the {@link FormComponent.submitSelector}, then it will
   * request a submit event from the form.
   *
   * @param event The event captured
   */
  @HostListener('click', [ '$event' ])
  onClick(event: PointerEvent): void {
    let target = event.target as HTMLElement
    if (target.matches(this.submitSelector)) {
      this._formRef.nativeElement.requestSubmit()
    }
  }

  /**
   * Lifecycle hook for when an element in the component is focused and the ENTER key is pressed down.
   *
   * This will then request the form be submitted.
   *
   * @param event The event captured
   */
  @HostListener('keydown.enter', [ '$event' ])
  onEnterKeyDown(event: KeyboardEvent): void {
    // If the enter button is coming from a textarea, break
    let target = event.target as HTMLElement
    if (target.matches('textarea, gpei-textarea')) {
      return
    }

    // Request the form be submitted
    this._formRef.nativeElement.requestSubmit()
  }

  /**
   * Lifecycle hook for when the {@link HTMLSlotElement} are updated.
   *
   * It will register all the events of the slotted child nodes, so that they trigger the other
   * lifecycle hooks of the component.
   */
  onSlottedContentChanged(): void {
    // Reset the current form data
    this._formData = new FormData()
    if (this.redirect) {
      this._formData.set('redirect', this.redirect)
    }

    // Unlisten to input events of slotted elements
    this._unlistenCallbacks.forEach(it => it())

    // listen for input events
    this._unlistenCallbacks = this._slottedElements?.reduce((callbacks, it) => {
      // Detect input events on inputs
      let unlistenInputCallback = this._renderer2.listen(it, 'input', it => this.onInput(it))
      callbacks.push(unlistenInputCallback)

      const setFormData = (element: Element) => {
        // Get the initial value of the element
        if ('name' in element && 'value' in element && element.name && element.value) {
          this._formData.set(element.name as string, element.value as string)
        }
        else {
          // Recursive call for child elements
          for (let child of element.children) {
            setFormData(child)
          }
        }
    }
    setFormData(it)
    return callbacks
    }, [])
  }

  /**
   * Check the validity of the form and all its slotted child nodes.
   *
   * @returns `TRUE` if the slotted child nodes and the form element are all valid
   */
  @Input()
  @HostBinding()
  checkValidity(): boolean {
    const checkValidityRecursive = (element: Element) => {
      // If the element itself can check the validity
      if ('checkValidity' in element && typeof element.checkValidity === 'function') {
        return element.checkValidity()
      }

      // Else check all it's children for validity
      else if (element.children.length > 0) {
        return [...element.children].every(it => checkValidityRecursive(it))
      }

      // Else it has no concept of validity, so we treat it as valid
      return true
    }

    let slottedElementsValid = this._slottedElements.every(it => checkValidityRecursive(it))
    let shadowedElementsValid = this._formRef?.nativeElement?.checkValidity?.() ?? true
    return slottedElementsValid && shadowedElementsValid
  }

  /**
   * Check and report the validity of the form and all its slotted child nodes.
   *
   * @returns `TRUE` if the slotted child nodes and the form element are all valid
   */
  @Input()
  @HostBinding()
  reportValidity(): boolean {
    const reportValidityRecursive = (element: Element) => {
      // If the element itself can report the validity
      if ('reportValidity' in element && typeof element.reportValidity === 'function') {
        return element.reportValidity()
      }

      // Elseget all of it's children to report validity
      else if (element.children.length > 0) {
        return [...element.children]
          .map(it => reportValidityRecursive(it))
          .every(it => it)
      }

      // Else it has no concept of validity, so we treat it as valid
      return true
    }

    /**
     * We use the map method instead of every because we want to report the validity on every field
     */
    let slottedElementsValid = this._slottedElements
      .map(it => reportValidityRecursive(it))
      .every(it => it)

    let shadowedElementsValid = this._formRef?.nativeElement?.reportValidity() ?? true
    return slottedElementsValid && shadowedElementsValid
  }

  @Input()
  @HostBinding()
  set redirect(value: string) {
    this._formData.set('redirect', value)
    this._redirect = value
  }

  get redirect(): string {
    return this._redirect
  }

  @Input()
  @HostBinding()
  set promptOnLeave(value: boolean | string) {
      let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._promptOnLeave = pendingValue
  }

  get promptOnLeave(): boolean {
    return this._promptOnLeave
  }

  @Input()
  @HostBinding()
  set debounceTime(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._debounceTimer) {
      this._debounceTimer = pendingValue
    }
  }

  get debounceTime(): number {
    return this._debounceTimer
  }

  @Input()
  @HostBinding()
  get value(): FormData {
    return this._formData
  }

  private get _slottedElements(): Element[] {
    return [
      ...this._slottedInputs,
      ...this._slottedActions
    ]
  }

  private get _slottedInputs(): Element[] {
    return this._inputSlotRef.nativeElement.assignedElements()
  }

  private get _slottedActions(): Element[] {
    return this._actionsSlotRef.nativeElement.assignedElements()
  }
}

/**
 * @public
 */
export const registerFormElement = registerCustomElementFactory(FormComponent)
